import type { ComponentProps } from 'react';

import { EditMarker } from '@eneco/cms';
import logger from '@eneco/common/log';
import * as SparkyIllustrations from '@eneco/sparky/illustrations';
import { Placeholder } from '@eneco/sparky/illustrations';

type IllustrationComponent = typeof Placeholder;

const marked = new Map<string, IllustrationComponent>();

/**
 * The illustration wrapped in an `EditMarker` naming the field it came from.
 *
 * An illustration is an SVG drawn from a lookup key, so nothing in the DOM says which field it
 * shows and Visual Editing has nothing to attach a click to. The marker is what lets the enclosing
 * block find it and put `data-sanity` on the right element. Outside draft mode `EditMarker` renders
 * only its children, so published markup is unchanged.
 *
 * Cached per name because callers invoke `getIllustration` during render: a new component type on
 * every call would make React unmount and remount the illustration each time.
 */
const withEditMarker = (name: string, Illustration: IllustrationComponent): IllustrationComponent => {
  const cached = marked.get(name);
  if (cached) return cached;

  const Marked = (props: ComponentProps<IllustrationComponent>) => (
    <EditMarker kind="illustration" match={name}>
      <Illustration {...props} />
    </EditMarker>
  );
  const component = Marked as unknown as IllustrationComponent;
  marked.set(name, component);
  return component;
};

/**
 * Resolve a CMS illustration name to its design-system component.
 *
 * Mirrors `@sitecore/common`'s `IllustrationHandler`. It lives here rather than in `@eneco/cms`
 * because `lib:cms` may not depend on `lib:sparky` and `lib:cms-components` may — so the components
 * that need illustrations resolve them directly, with no indirection to thread through.
 *
 * An unknown name logs and falls back to Sparky's own `Placeholder`, matching the legacy behaviour:
 * a name the design system dropped should be visible as a gap, not silently blank.
 */
export const getIllustration = (name: string | undefined): IllustrationComponent | undefined => {
  if (!name) return undefined;
  if (name in SparkyIllustrations) {
    return withEditMarker(name, (SparkyIllustrations as Record<string, IllustrationComponent>)[name]);
  }
  logger.error('wVRxzv', `Illustration ${name} could not be found. A placeholder will be rendered.`);
  // Marked under the requested name, not "Placeholder": the field still holds that name, and an
  // editor looking at a grey square is exactly who needs the click to reach it.
  return withEditMarker(name, Placeholder);
};
